import { Suspense, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseJoinKeyQuery } from '#api/join-keys.queries.ts';
import { DeleteIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { HelpText } from '#components/atoms/forms/index.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { DetailList } from '#components/patterns/detail-list/DetailList.tsx';
import { DetailListRow } from '#components/patterns/detail-list/DetailListRow.tsx';
import { NetworkLink } from '#components/patterns/entity-links/network-link/NetworkLink.tsx';
import { RoleLink } from '#components/patterns/entity-links/role-link/RoleLink.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { InlineTagsList } from '#components/patterns/tags/InlineTagsList.tsx';
import { ExpiresAt } from '#screens/settings/api-keys/detail/ExpiresAt.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { DeleteJoinKeyDialog } from '../delete/DeleteJoinKeyDialog.tsx';
import { JoinKeyForm } from '../form/JoinKeyForm.tsx';
import styles from './JoinKeyDetailPage.module.css';

export function JoinKeyDetailPage() {
  const { joinKeyID } = useParams() as { joinKeyID: string };
  const {
    data: { data: joinKey },
    error,
    isFetching,
  } = useSuspenseJoinKeyQuery(joinKeyID);
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if this has been deleted (e.g. in a different tab)
  if (error && !isFetching && error instanceof MissingResourceError) {
    throw error;
  }

  return (
    <Titled title={(title) => fromArray([joinKey.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to="/settings/join-keys" className="a">
                  Host join keys
                </Link>
                {' / '}
                <span>{joinKey.name}</span>
              </>
            }
            subtitle={
              <span className={styles.JoinKeyDetailPage_subtitle}>
                <span className="mb-8">
                  ID: <CopyPill value={joinKey.id} />
                </span>
                <span className="mb-8">
                  <ExpiresAt expiresAt={joinKey.expiresAt} withLabel={true} />
                </span>
              </span>
            }
          >
            <ButtonGroup>
              <ButtonGroupItem>
                <Tooltip
                  content="Revoke"
                  render={<Button size="small" variant="icon" onClick={() => setIsDeleting(true)} />}
                >
                  <DeleteIcon aria-hidden="true" />
                  <span className="visually-hidden">Revoke</span>
                </Tooltip>
              </ButtonGroupItem>
            </ButtonGroup>
          </PageHeader>
        }
      >
        <HelpText className="mb-16">
          Every host enrolled with this key gets the following. It can’t be changed after the key is created; create a
          new key instead.
        </HelpText>
        <DetailList className="mb-40">
          <DetailListRow label="Network" value={<NetworkLink networkId={joinKey.networkID} />} />
          <DetailListRow label="Role" value={joinKey.roleID ? <RoleLink roleID={joinKey.roleID} /> : 'No role'} />
          <DetailListRow
            label="Tags"
            value={
              joinKey.tags.length ?
                <Suspense fallback={null}>
                  <InlineTagsList tagNames={joinKey.tags} linkTags={true} />
                </Suspense>
              : 'None'
            }
          />
          <DetailListRow label="Addresses from" value={joinKey.autoAssignCIDRs.join(', ')} />
        </DetailList>
        <JoinKeyForm
          id={joinKey.id}
          onSuccess={() => void navigate('/settings/join-keys')}
          defaultValues={{ name: joinKey.name, expiresAt: joinKey.expiresAt }}
        />
        {isDeleting ?
          <DeleteJoinKeyDialog
            joinKeyID={joinKeyID}
            onDelete={() => void navigate('/settings/join-keys', { replace: true })}
            onCancel={() => setIsDeleting(false)}
          />
        : null}
      </MainLayout>
    </Titled>
  );
}
